/**
 * Crew-thread helpers shared with the phone overlay.
 * Status flips and system rows are not conversation.
 */
import type { CrewChatMessage } from '../../services/crewChatApi';

const STATUS_FLIP = /changed to\s+(on|off)\b/i;

export function isCrewChatMessage(message: CrewChatMessage): boolean {
  const type = message.message_type || 'text';
  if (type === 'system') return false;
  // Vehicle lines are posted as `alert` so the phone speaks them. They still
  // belong in this thread — that is the copy the crew already has open.
  if (type === 'alert') return vehicleLinePriority(message.message || '') != null;
  if (STATUS_FLIP.test(message.message || '')) return false;
  return true;
}

export function decodeChatText(value: string): string {
  return value
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&#x27;/g, "'");
}

/** Same words the map bar speaks, so Chat and the vehicle stay in step. */
export function formatVehicleCrewLine(message: string, urgent: boolean): string {
  return `${urgent ? 'URGENT' : 'NORMAL'} — ${message}`;
}

export function vehicleLinePriority(text: string): 'urgent' | 'normal' | null {
  if (/^URGENT\s+[—–-]\s+/.test(text)) return 'urgent';
  if (/^NORMAL\s+[—–-]\s+/.test(text)) return 'normal';
  return null;
}

export function stripVehiclePriority(text: string): string {
  return text.replace(/^(?:URGENT|NORMAL)\s+[—–-]\s+/, '');
}

/** Oldest first so the newest row sits next to the compose bar. */
export function inChatOrder(list: CrewChatMessage[]): CrewChatMessage[] {
  return list.slice().sort((a, b) => {
    const at = new Date(a.created_at || 0).getTime();
    const bt = new Date(b.created_at || 0).getTime();
    if (at !== bt) return at - bt;
    return (a.id || 0) - (b.id || 0);
  });
}

export function upsertMessage(
  list: CrewChatMessage[],
  incoming: CrewChatMessage,
): CrewChatMessage[] {
  const idx = list.findIndex((m) => m.id === incoming.id);
  if (idx === -1) return inChatOrder([...list, incoming]);
  const next = list.slice();
  next[idx] = incoming;
  return next;
}
